import { FormEvent, useState } from 'react';

import { getErrorMessage } from '@/hooks/useApi';

import { useRedeemGiftCode } from '../hooks/useGiftCodes';

export default function GiftCodeCard() {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ tokensGranted: number; newBalance: number } | null>(
    null
  );

  const redeemCode = useRedeemGiftCode();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const trimmedCode = code.trim();
    if (!trimmedCode) {
      setError('Please enter a gift code');
      return;
    }

    // UUID format validation (basic check)
    if (trimmedCode.length < 36) {
      setError('Invalid gift code format');
      return;
    }

    try {
      const result = await redeemCode.mutateAsync(trimmedCode);
      setSuccess({
        tokensGranted: result.tokensGranted,
        newBalance: result.newBalance,
      });
      setCode('');
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-6">
      <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <svg
          className="w-5 h-5 text-purple-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"
          />
        </svg>
        Redeem Gift Code
      </h2>

      {/* Success message */}
      {success && (
        <div className="mb-4 p-4 bg-green-500/20 border border-green-500/30 rounded-lg">
          <p className="text-green-400 font-medium">Gift code redeemed!</p>
          <p className="text-green-300 text-sm mt-1">
            +{success.tokensGranted} tokens added. New balance: {success.newBalance} tokens
          </p>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="gift-code" className="block text-sm font-medium text-purple-300 mb-2">
            Enter your gift code
          </label>
          <input
            id="gift-code"
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX"
            className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-slate-400 focus:outline-none focus:border-purple-500 font-mono text-sm"
            disabled={redeemCode.isPending}
          />
          <p className="mt-1.5 text-xs text-slate-400">
            Gift codes grant free tokens when redeemed
          </p>
        </div>

        <button
          type="submit"
          disabled={redeemCode.isPending || !code.trim()}
          className="w-full bg-purple-600 hover:bg-purple-500 disabled:bg-purple-600/50 disabled:cursor-not-allowed text-white font-medium py-3 rounded-lg transition-colors"
        >
          {redeemCode.isPending ? 'Redeeming...' : 'Redeem Code'}
        </button>
      </form>
    </div>
  );
}
